/**
 * App.jsx - Main Application Component
 * 
 * @description
 * Root component that sets up the routing for the entire Link Spaghetti application.
 * Wraps all routes in an ErrorBoundary for graceful error handling.
 * 
 * @routes
 * - "/" - HomePage (public landing page)
 * - "/signin" - User sign in page
 * - "/signup" - User registration page
 * - "/dashboard" - User dashboard (requires authentication)
 * - "/upgrade" - Subscription upgrade page
 * - "/admin/login" - Admin login page (internal use only)
 * - "/admin/dashboard" - Admin dashboard (requires admin auth)
 * - "/:username" - Public profile page (dynamic route)
 * 
 * @dependencies
 * - react-router-dom: For client-side routing
 * - ErrorBoundary: Custom error handling component
 * 
 * @example
 * // To add a new route:
 * <Route path="/new-route" element={<NewComponent />} />
 */

import './utils/environment.js'; // Initialize environment checks
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import HomePage from './pages/HomePage';
import SignIn from './pages/SignIn';
import SignUp from './pages/SignUp';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import EmailVerification from './pages/EmailVerification';
import Dashboard from './pages/Dashboard';
import PublicProfile from './pages/PublicProfile';
import Upgrade from './pages/Upgrade';
import AdminLogin from './pages/admin/AdminLogin';
import AdminDashboard from './pages/admin/AdminDashboard';
import ErrorBoundary from './components/ErrorBoundary';
import CookieConsent from './components/CookieConsent';
import TermsAndConditions from './pages/TermsAndConditions';
import PrivacyPolicy from './pages/PrivacyPolicy';
import CookiePolicy from './pages/CookiePolicy';
import TestPage from './TestPage';

function App() {
  return (
    <ErrorBoundary>
      <Router>
        <div className="min-h-screen bg-white">
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/signin" element={<SignIn />} />
            <Route path="/signup" element={<SignUp />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/verify-email" element={<EmailVerification />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/upgrade" element={<Upgrade />} />
            <Route path="/terms" element={<TermsAndConditions />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/cookies" element={<CookiePolicy />} />
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin/dashboard" element={<AdminDashboard />} />
            <Route path="/api-test" element={<TestPage />} />
            {/* Catch-all route for public profiles - MUST be last */}
            <Route path="/:username" element={<PublicProfile />} />
          </Routes>
          <CookieConsent />
        </div>
      </Router>
    </ErrorBoundary>
  );
}

export default App;
